import { PurchaseOrderData, POItem, POCharge } from './po-pdf.service';

function formatDate(raw: string | null | undefined): string {
  if (!raw) return '';
  const d = new Date(raw);
  return isNaN(d.getTime())
    ? raw
    : d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function buildPaymentTermsLabel(terms: any[]): string {
  if (!terms?.length) return '';
  return terms
    .map(t => {
      const label = String(t.description || '').trim();
      const perc = t.paymentPerc != null ? `${t.paymentPerc}%` : '';
      const days = t.days != null ? `${t.days} day${t.days === 1 ? '' : 's'}` : '';
      return [label, perc, days].filter(Boolean).join(' ').trim();
    })
    .filter(Boolean)
    .join(', ');
}

function mapGSTType(value: any): string {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return String(value);
}

export function mapApiToPOData(api: any): PurchaseOrderData {

  const items: POItem[] = (api.purchaseOrderDetails ?? []).map((d: any, idx: number) => ({
    srNo:        idx + 1,
    description: d.description || d.productName,
    qty:         d.poQty,
    unit:        d.unit ?? 'Nos',
    rate:        d.rate,
    discPercent: d.discountPercent,
    gstPercent:  d.gstPercent,
    basicAmt:    d.amount,
    total:       d.netAmount,
  }));

  // FIX: compute total per charge when API returns null,
  // and map API heading to the charge description when provided
  const charges: POCharge[] = (api.otherCharges ?? []).map((c: any, idx: number) => {
    const gstAmt = ((c.amount ?? 0) * (c.gstPercent ?? 0)) / 100;
    const computedTotal = c.total != null ? c.total : (c.amount ?? 0) + gstAmt;
    return {
      srNo:        idx + 1,
      description: c.heading || c.description || '',
      gstPercent:  c.gstPercent,
      amount:      c.amount,
      total:       computedTotal,
    };
  });

  // FIX: compute totalAdditionalCharges from the fixed charges array (not from raw api)
  const totalAdditionalCharges = charges.reduce((s: number, c: POCharge) => s + (c.total ?? 0), 0);
   const approvals = (api.approvals ?? [])
    .sort((a: any, b: any) => (a.level ?? 0) - (b.level ?? 0))
    .map((a: any) => ({
      level: a.level,
      employeeName: a.employeeName,
      designation: a.designation,
      status: a.status,
      signature: a.signature
        ? `data:image/png;base64,${a.signature}`
        : null
    }));


  return {
    poNo:   api.purchaseOrderNo,
    poDate: formatDate(api.podate),

    company: {
      name:    api.companyDetails?.name ?? api.companyName ?? 'Tej Group',
      address: api.companyDetails?.address ?? api.companyAddress ?? '',
      gstin:   api.companyDetails?.gstin ?? api.companyGst ?? '27AAECT9572F1ZY',
      logoUrl: api.companyUrl, // FIX: logoUrl is not provided by API, so we leave it null for now
    },

    supplier: {
      ...(api.supplierDetails || {
        name:          api.supplierName || `Supplier #${api.supplier}`,
        supplierCode:  api.supplierCode != null ? String(api.supplierCode) : String(api.supplier),
        contactPerson: api.supplierContactPerson || '',
        mobile:        api.supplierMobile || '',
        email:         api.supplierEmail || '',
        address:       api.supplierAddress || '',
      }),
      gstNumber: (
        api.supplierDetails?.gstNumber ||
        api.supplierDetails?.gstNo ||
        api.supplierGstNumber ||
        api.supplierGSTNumber ||
        api.supplierGstNo ||
        api.supplierGstin ||
        api.gstNumber ||
        ''
      ),
    },

    project: {
      projectName:      api.siteName         ?? '',
      projectCode:      String(api.siteKey   ?? ''),
      prNo:             (api.prn ?? []).join(', '),
      department:       api.warehouseName    ?? '',
      deliverySchedule: api.deliverySchedule ?? 'As agreed',
      paymentTerms:     buildPaymentTermsLabel(api.paymentTermsResponses),
      deliveryAddress:  api.deliveryAddress  ?? '',
      gstType:          mapGSTType(api.gstType ?? api.gst_type),
      sameState:        api.sameState ?? api.same_state ?? api.isSameState ?? undefined,
    },

    summary: {
      materialSubTotal:       api.materialSubTotal ?? api.subTotal ?? api.totalAmount ?? api.totalSupply ?? 0,
      totalDiscount:          api.totalDiscount ?? api.discountAmount ?? 0,
      totalAdditionalCharges, // FIX: use locally computed value
      totalGST:               api.gstAmount      ?? api.totalGST ?? api.totalGst ?? 0,
      roundOff:               api.roundOff        ?? 0,
      grandTotal:             api.grossAmount      ?? api.totalAmount ?? api.grandTotal ?? 0,
    },

    items,
    charges,
    approvals,

    amountInWords: api.amountInWords || api.amountInwords || api.amountWords || `Rupees ${(api.grossAmount ?? 0).toLocaleString('en-IN')} Only`,
    preparedBy:    api.siteEngName || undefined,
    commercialTerms: (api.termsConditions ?? api.epTermsResponses ?? []).map((term: any) => ({
      heading: term.heading || term.description || `Term #${term.erTermsKey ?? ''}`,
      description: term.description || term.heading || '',
    })),

  };
}
